import { apiFetch } from '@/lib/api';
import type {
  AcordaoOverview,
  AnatelOverview,
  CitationOption,
  ConselhoBench,
  ConselhoItems,
  ConselhoScopeCases,
  ConsistencyOverview,
  ContradictionList,
  Coverage,
  DecisionFilters,
  DecisionAuthorityPosture,
  DecisionDoctrine,
  DecisionList,
  DocumentFilters,
  DocumentSearchList,
  DocumentTypeOption,
  EntityList,
  EntityProfile,
  InfracaoFilters,
  InfracaoOverview,
  PafCaseDetail,
  PafConsistencyOverview,
  PafContradictionList,
  PafDecisionDoctrine,
  PafDecisionFilters,
  PafDecisionList,
  PafOverview,
  ProcessDetail,
  PropositionFilters,
  PropositionSearchList,
  PersonProfile,
  SanctionIntelligence,
} from '@/services/types';

export function fetchAnatelOverview(): Promise<AnatelOverview> {
  return apiFetch<AnatelOverview>('/api/v1/anatel/overview');
}

/**
 * POST rather than GET: the filter set is a body, and an export must be able to
 * replay the SAME request. A separate export query is how a spreadsheet starts
 * disagreeing with the screen it came from.
 */
export function searchDecisions(filters: DecisionFilters): Promise<DecisionList> {
  return apiFetch<DecisionList>('/api/v1/anatel/decisions', {
    method: 'POST',
    body: JSON.stringify(filters),
  });
}

/**
 * Search the reasoning layer: which decisions have held what.
 *
 * POST for the same reason as the decision search -- the filter set is a body an
 * export must be able to replay verbatim. Submit-driven rather than
 * search-as-you-type: `proposition_text` has no index in `pg-anatel` and this
 * application cannot add one, so every query is a ~1.2s scan of 68,843 rows.
 */
export function searchPropositions(filters: PropositionFilters): Promise<PropositionSearchList> {
  return apiFetch<PropositionSearchList>('/api/v1/anatel/propositions', {
    method: 'POST',
    body: JSON.stringify(filters),
  });
}

/**
 * Search the raw document corpus: 2.46M documents, text only.
 *
 * Submit-driven. The count is fast but the ranked page is not, and the corpus is
 * wide enough that a keystroke-triggered search would queue requests behind each
 * other for no benefit.
 */
export function searchDocuments(filters: DocumentFilters): Promise<DocumentSearchList> {
  return apiFetch<DocumentSearchList>('/api/v1/anatel/documents', {
    method: 'POST',
    body: JSON.stringify(filters),
  });
}

/** The commonest document types. Capped: 325 distinct values exist. */
export function fetchDocumentTypes(): Promise<DocumentTypeOption[]> {
  return apiFetch<DocumentTypeOption[]>('/api/v1/anatel/document-types');
}

/**
 * The collegiate track. Dissent COUNTS only -- the rate is refused by the API
 * with the measurement behind the refusal.
 */
export function fetchAcordaos(params: {
  onlyWithDissent: boolean;
  limit: number;
  offset: number;
}): Promise<AcordaoOverview> {
  const query = new URLSearchParams({
    only_with_dissent: String(params.onlyWithDissent),
    limit: String(params.limit),
    offset: String(params.offset),
  });
  return apiFetch<AcordaoOverview>(`/api/v1/anatel/acordaos?${query}`);
}

/**
 * Infraction detail and payment status. POST because the filter set is a body
 * an export must be able to replay verbatim.
 */
export function fetchInfracoes(filters: InfracaoFilters): Promise<InfracaoOverview> {
  return apiFetch<InfracaoOverview>('/api/v1/anatel/infracoes', {
    method: 'POST',
    body: JSON.stringify(filters),
  });
}

/** Outcome vocabulary read from the data, never hardcoded in the UI. */
export function fetchOutcomeCodes(): Promise<string[]> {
  return apiFetch<string[]>('/api/v1/anatel/outcome-codes');
}

/** Deciding offices read from the data, never hardcoded in the UI. */
export function fetchDecidingAuthorities(): Promise<string[]> {
  return apiFetch<string[]>('/api/v1/anatel/deciding-authorities');
}

/**
 * The two panels above the decision list, on their own endpoints. Both are
 * corpus-wide and independent of every filter, and the doctrine aggregation
 * is the costliest query the screen runs -- inside the list payload it ran
 * again on every page turn and every keystroke of the search.
 */
export function fetchDecisionDoctrine(): Promise<DecisionDoctrine> {
  return apiFetch<DecisionDoctrine>('/api/v1/anatel/decisions/citation-lines');
}

export function fetchDecisionAuthorityPosture(): Promise<DecisionAuthorityPosture> {
  return apiFetch<DecisionAuthorityPosture>('/api/v1/anatel/decisions/authority-posture');
}

/** `key` is the DIGITS-ONLY process number -- see DecisionListItem.process_key. */
export function fetchProcess(key: string): Promise<ProcessDetail> {
  return apiFetch<ProcessDetail>(`/api/v1/anatel/processes/${encodeURIComponent(key)}`);
}

export function searchEntities(filters: {
  query?: string | null;
  limit?: number;
  offset?: number;
}): Promise<EntityList> {
  return apiFetch<EntityList>('/api/v1/anatel/entities', {
    method: 'POST',
    body: JSON.stringify(filters),
  });
}

/** `cnpj` is the digits-only CNPJ -- a complete natural key in this corpus. */
export function fetchEntity(cnpj: string): Promise<EntityProfile> {
  return apiFetch<EntityProfile>(`/api/v1/anatel/entities/${encodeURIComponent(cnpj)}`);
}

/**
 * Corpus coverage. Requested SEPARATELY from the overview: counting the 1.8M-row
 * registry costs seconds, which is fine for a panel that fills in on its own and
 * not for the landing page's critical path.
 */
export function fetchCoverage(): Promise<Coverage> {
  return apiFetch<Coverage>('/api/v1/anatel/coverage');
}

/** The fiscal track. A separate corpus: tax credits, not fines. */
export function fetchPafOverview(): Promise<PafOverview> {
  return apiFetch<PafOverview>('/api/v1/anatel/paf/overview');
}

/**
 * One PAF relator, keyed by a slug derived from their NAME.
 *
 * The only person-scoped page in the product: PAF is the one ANATEL table where
 * a named adjudicator and an outcome share a row.
 */
export function fetchAnatelConselheiro(slug: string): Promise<PersonProfile> {
  return apiFetch<PersonProfile>(`/api/v1/anatel/r/${encodeURIComponent(slug)}`);
}

export function fetchAnatelConselho(): Promise<ConselhoBench> {
  return apiFetch<ConselhoBench>('/api/v1/anatel/conselho');
}

export function fetchAnatelConselhoItems(): Promise<ConselhoItems> {
  return apiFetch<ConselhoItems>('/api/v1/anatel/conselho/items');
}

/** The cases behind one member's severity row; `scope` is the metric's own string. */
export function fetchAnatelConselhoCases(member: string, scope: string): Promise<ConselhoScopeCases> {
  const query = new URLSearchParams({ member, scope });
  return apiFetch<ConselhoScopeCases>(`/api/v1/anatel/conselho/cases?${query}`);
}

export function fetchPafDecisions(params: {
  query?: string | null;
  relator?: string | null;
  outcome?: string | null;
  year?: string | null;
  citation?: string | null;
  limit: number;
  offset: number;
}): Promise<PafDecisionList> {
  const search = new URLSearchParams({
    limit: String(params.limit),
    offset: String(params.offset),
  });
  if (params.query) search.set('query', params.query);
  if (params.relator) search.set('relator', params.relator);
  if (params.outcome) search.set('outcome', params.outcome);
  if (params.year) search.set('year', params.year);
  if (params.citation) search.set('citation', params.citation);
  return apiFetch<PafDecisionList>(`/api/v1/anatel/paf/decisions?${search}`);
}

export function fetchPafDecisionFilters(): Promise<PafDecisionFilters> {
  return apiFetch<PafDecisionFilters>('/api/v1/anatel/paf/decision-filters');
}

/** PAF's doctrine panel, fetched once like PADO's. */
export function fetchPafDecisionDoctrine(): Promise<PafDecisionDoctrine> {
  return apiFetch<PafDecisionDoctrine>('/api/v1/anatel/paf/decisions/citation-lines');
}

/** `key` is the digits-only process number. */
export function fetchPafCase(key: string): Promise<PafCaseDetail> {
  return apiFetch<PafCaseDetail>(`/api/v1/anatel/paf/cases/${encodeURIComponent(key)}`);
}

export function fetchConsistencyOverview(): Promise<ConsistencyOverview> {
  return apiFetch<ConsistencyOverview>('/api/v1/anatel/consistency');
}

export function fetchContradictions(params: {
  onlyFullyVerified: boolean;
  citation?: string | null;
  limit: number;
  offset: number;
}): Promise<ContradictionList> {
  const query = new URLSearchParams({
    only_fully_verified: String(params.onlyFullyVerified),
    limit: String(params.limit),
    offset: String(params.offset),
  });
  if (params.citation) {
    query.set('citation', params.citation);
  }
  return apiFetch<ContradictionList>(`/api/v1/anatel/consistency/pairs?${query}`);
}

/** Grounds the queue's pairs actually share -- options read from the data. */
export function fetchConsistencyCitations(): Promise<string[]> {
  return apiFetch<string[]>('/api/v1/anatel/consistency/citations');
}

/**
 * The queue's query params as a URLSearchParams, so the export replays the
 * SAME query the screen ran -- the parity rule this product enforces on every
 * export.
 */
export function anatelConsistencyParams(params: {
  onlyFullyVerified: boolean;
  citation?: string | null;
}): URLSearchParams {
  const query = new URLSearchParams({
    only_fully_verified: String(params.onlyFullyVerified),
  });
  if (params.citation) {
    query.set('citation', params.citation);
  }
  return query;
}

/** Citations that can back a fine distribution. Boilerplate is flagged, not hidden. */
export function fetchCitations(): Promise<CitationOption[]> {
  return apiFetch<CitationOption[]>('/api/v1/anatel/citations');
}

export function fetchSanctionIntelligence(query: {
  citations: string[];
  match_all: boolean;
  subject_amount?: number | null;
}): Promise<SanctionIntelligence> {
  return apiFetch<SanctionIntelligence>('/api/v1/anatel/sanctions', {
    method: 'POST',
    body: JSON.stringify(query),
  });
}


export function fetchPafConsistencyOverview(): Promise<PafConsistencyOverview> {
  return apiFetch<PafConsistencyOverview>('/api/v1/anatel/paf/consistency');
}

export function fetchPafContradictions(params: {
  onlyFullyVerified: boolean;
  citation?: string | null;
  limit: number;
  offset: number;
}): Promise<PafContradictionList> {
  const query = new URLSearchParams({
    only_fully_verified: String(params.onlyFullyVerified),
    limit: String(params.limit),
    offset: String(params.offset),
  });
  if (params.citation) {
    query.set('citation', params.citation);
  }
  return apiFetch<PafContradictionList>(`/api/v1/anatel/paf/consistency/pairs?${query}`);
}

/** Grounds the fiscal queue's pairs actually share -- options from the data. */
export function fetchPafConsistencyCitations(): Promise<string[]> {
  return apiFetch<string[]>('/api/v1/anatel/paf/consistency/citations');
}

/** The fiscal queue's query params, so the export replays the screen's query. */
export function anatelPafConsistencyParams(params: {
  onlyFullyVerified: boolean;
  citation?: string | null;
}): URLSearchParams {
  const query = new URLSearchParams({
    only_fully_verified: String(params.onlyFullyVerified),
  });
  if (params.citation) {
    query.set('citation', params.citation);
  }
  return query;
}
